// vim: set ts=2 sts=2 sw=2 et:

import React from "react";
import { batch } from "react-redux";

import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Form from "react-bootstrap/Form";

import BarLoad from "../../components/lifting/BarLoad";
import LeftCard from "../../components/lifting/LeftCard";
import LiftingHeader from "../../components/lifting/LiftingHeader";
import LiftingTable, { globalFocusAttemptInputId } from "../../components/lifting/LiftingTable";
import WeighinsView from "../../components/weighins/WeighinsView";
import { getEntryWeightClassLabel } from "../../reducers/meetReducer";
import { isEntryPresent, liftToAttemptFieldName, liftToStatusFieldName } from "../../logic/entry";
import { getLiftingOrder } from "../../logic/liftingOrder";
import { selectPlates } from "../../logic/barLoad";
import { assertFlight, assertLift } from "../../types/utils";
import { displayNumber, displayWeightOnePlace, kg2lbs } from "../../logic/units";
import { getString, localizeFlight } from "../../logic/strings";
import { Entry, Flight, Lift } from "../../types/dataTypes";
import { LiftingState } from "../../types/stateTypes";
import {
  JUDGE_POSITIONS,
  JudgePosition,
  JudgeVoteValue,
  JudgeVotes,
  resolveJudgeVotes,
} from "../judging/resolveJudgeVotes";
import { canUseBrowserWebSocket, getBrowserPlatformLiveWebSocketUrl } from "../live/browserPlatformLiveClient";
import { buildAttemptSeries, buildLiftSeries, RecordCategoryLookup } from "../live/buildLiftSeries";
import {
  DmxStatus,
  PlatformLiveAttempt,
  PlatformLiveBroadcastInfo,
  PlatformLiveAttemptClock,
  PlatformLiveServerMessage,
  PlatformLiveState,
  makeAttemptKey as makePlatformLiveAttemptKey,
} from "../live/platformLiveProtocol";
import LiveConnectionIndicators from "../live/LiveConnectionIndicators";
import { getRemainingClockSeconds } from "../live/platformLiveClock";
import { Link } from "react-router-dom";
import { faForward } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlatformRef } from "../types";
import { OperationLayerContainerProps } from "./OperationLayerContainer";
import OperationAttemptInput from "./OperationAttemptInput";
import { notifyOperator, requestOperatorNotificationPermission } from "./operatorAttention";
import { evaluateRecordAttempt, RecordSuggestion } from "../records/recordEvaluator";
import { resolveRecordCategory } from "../records/recordMarkingPolicy";
import { selectEnabledRecordCategories } from "../records/wrpfRecordsReducer";
import { recordFieldLabel } from "../records/recordLabels";
import {
  highestRecordCategory,
  recordAttemptAriaLabel,
  recordAttemptLabel,
  recordCategoryDataAttributes,
  recordCategoryFromJurisdiction,
  WRPF_RECORD_CATEGORY_PRIORITY,
} from "../records/recordCategories";
import type { WrpfRecordCategory } from "../handoff/types";

import styles from "./OperationLayer.module.scss";
import liftingViewStyles from "../../components/lifting/LiftingView.module.scss";
import liftingFooterStyles from "../../components/lifting/LiftingFooter.module.scss";

interface InternalState {
  flight: Flight;
  lift: Lift;
  overrideAttempt: number | null;
  overrideEntryId: number | null;
  replaceTableWithWeighins: boolean;
  judgeVotes: JudgeVotes;
  judgeVotesAttemptKey: string | null;
  liveStatus: "off" | "connecting" | "connected" | "rejected" | "disconnected" | "reconnecting";
  liveError: string | null;
  dmxStatus: DmxStatus;
  attemptClock: PlatformLiveAttemptClock | null;
  serverNowMs: number | null;
  clockReceivedAtMs: number | null;
  clockTick: number;
  manualLiftLocked: boolean;
  // Manual-verdict mode: the judges' computed verdict is held here (lights still show live) until the
  // operator presses "Confirmar veredicto". null when there is nothing awaiting confirmation.
  pendingVerdict: {
    attemptKey: string;
    entryId: number;
    lift: Lift;
    attemptOneIndexed: number;
    result: boolean;
  } | null;
  // Classic ?lifting "manual next": after a verdict the scoresheet IS marked (cell turns green/red) and
  // the table advances locally, but the LIVE publish is suppressed so the audience screen shows the verdict
  // for its 2s reveal and then "Esperando" until the operator presses "Siguiente". true while a committed
  // verdict is awaiting that advance.
  awaitingNext: boolean;
  // The committed verdict (result + the votes/lights that produced it), shown in the OPERATOR's judging bar
  // for the whole hold (until "Siguiente") — even though the per-attempt votes were reset by markLift.
  heldVerdict: { result: boolean; votes: JudgeVotes } | null;
  recordContextMenu: {
    entryId: number;
    lift: Lift;
    attemptOneIndexed: number;
    x: number;
    y: number;
    categorySubmenuOpen: boolean;
  } | null;
  // Measured height (px) of the classic ?lifting fixed footer, so the page reserves exactly that much
  // bottom space and the footer never covers the lifters table.
  classicFooterHeight: number;
}

const LIFTS: ReadonlyArray<Lift> = ["S", "B", "D"];
const ATTEMPTS = [1, 2, 3, 4];
const MANUAL_LIFT_LOCK_MS = 3_000;

const recordLiftLabel = (liftType: RecordSuggestion["liftType"]): string =>
  ({
    SQUAT: "Sentadilla",
    BENCH_PRESS: "Press banca",
    DEADLIFT: "Peso muerto",
    TOTAL: "Total proyectado",
  })[liftType];

const recordJurisdictionLabel = (jurisdiction: RecordSuggestion["jurisdiction"]): string =>
  ({
    NOA: "Regional NOA",
    NEA: "Regional NEA",
    CUYO: "Regional Cuyo",
    PAMPEANA: "Regional Pampeana",
    PATAGONICA: "Regional Patagónica",
    NATIONAL: "Nacional",
    IBEROAMERICAN: "Iberoamericano",
    WORLD: "Mundial",
    INTERNATIONAL: "Internacional (legacy)",
  })[jurisdiction];

interface CurrentAttemptContext {
  effectiveFlight: Flight;
  entriesInFlight: Array<Entry>;
  orderedEntries: Array<Entry>;
  currentEntry: Entry | null;
  currentEntryId: number | null;
  attemptOneIndexed: number;
  nextEntry: Entry | null;
  nextEntryId: number | null;
  nextAttemptOneIndexed: number | null;
}

const isAssignedEntry = (entry: Entry, platformRef: PlatformRef): boolean => {
  return entry.day === platformRef.day && entry.platform === platformRef.platformId;
};

class OperationLayerView extends React.Component<OperationLayerContainerProps, InternalState> {
  constructor(props: OperationLayerContainerProps) {
    super(props);

    this.state = {
      flight: "A",
      lift: "S",
      overrideAttempt: null,
      overrideEntryId: null,
      replaceTableWithWeighins: false,
      judgeVotes: {},
      judgeVotesAttemptKey: null,
      liveStatus: "off",
      liveError: null,
      dmxStatus: "disabled",
      attemptClock: null,
      serverNowMs: null,
      clockReceivedAtMs: null,
      clockTick: Date.now(),
      manualLiftLocked: false,
      pendingVerdict: null,
      awaitingNext: false,
      heldVerdict: null,
      recordContextMenu: null,
      classicFooterHeight: 0,
    };
  }

  private liveSocket: WebSocket | null = null;
  private liveAccepted = false;
  private lastPublishedAttemptKey: string | null = null;
  private clockTimer: number | null = null;
  private manualLiftLockActive = false;
  private manualLiftLockTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private classicFooterRef = React.createRef<HTMLDivElement>();
  private operatorDockRef = React.createRef<HTMLElement>();
  private wasHoldingForNext = false;
  private pendingNextNotification: Notification | null = null;
  private recordContextMenuRef: HTMLDivElement | null = null;

  componentDidMount() {
    if (this.isLiveMode()) {
      this.connectLive();
    }
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.syncClassicFooterHeight);
      document.addEventListener("mousedown", this.handleDocumentMouseDown);
    }
    this.syncClassicFooterHeight();
    this.wasHoldingForNext = this.isHoldingForNext();
  }

  componentDidUpdate(prevProps: OperationLayerContainerProps) {
    this.syncClassicFooterHeight();
    this.notifyWhenVerdictAwaitsNext();
    if (!this.isFullPresentation() && this.state.recordContextMenu !== null) {
      this.setState({ recordContextMenu: null });
    }
    if (
      prevProps.locationSearch !== this.props.locationSearch ||
      prevProps.platformRef?.day !== this.props.platformRef?.day ||
      prevProps.platformRef?.platformId !== this.props.platformRef?.platformId
    ) {
      this.disconnectLive();
      if (this.isLiveMode()) {
        this.connectLive();
      }
      return;
    }

    this.publishCurrentAttemptIfNeeded();
  }

  componentWillUnmount() {
    this.pendingNextNotification?.close();
    this.disconnectLive();
    this.stopClockTicker();
    this.clearManualLiftLockTimer();
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.syncClassicFooterHeight);
      document.removeEventListener("mousedown", this.handleDocumentMouseDown);
    }
  }

  // Keep the page's bottom padding equal to the fixed classic-footer height (which grows/shrinks as the
  // judging controls wrap), so the footer reserves its space like the original bar and never covers the
  // table. Guarded so it only setStates when the height actually changes (no render loop).
  syncClassicFooterHeight = () => {
    const el = this.classicFooterRef.current ?? this.operatorDockRef.current;
    const height = el === null ? 0 : el.offsetHeight;
    if (height !== this.state.classicFooterHeight) {
      this.setState({ classicFooterHeight: height });
    }
  };

  // Fuera de Levantamientos (barra de tarima u oculta), la pantalla sigue en vivo pero no se ve.
  isFullPresentation = (): boolean => {
    return (this.props.presentation ?? "full") === "full";
  };

  isClassicLiftingWindow = (): boolean => {
    return this.props.classicLiftingWindow === true;
  };

  isLiveMode = (): boolean => {
    return this.isClassicLiftingWindow() || new URLSearchParams(this.props.locationSearch).get("live") === "1";
  };

  isJudgeVotingEnabled = (): boolean => {
    return this.props.liveFeatures?.judgeVoting !== false;
  };

  isManualVerdictMode = (): boolean => {
    return this.props.liveFeatures?.manualVerdictConfirmation === true;
  };

  isSimulatorMode = (): boolean => {
    return new URLSearchParams(this.props.locationSearch).get("judgeSimulator") === "1";
  };

  getFlights = (): Array<Flight> => {
    const flights: Array<Flight> = [];
    for (let i = 0; i < this.props.assignedEntries.length; i++) {
      const flight = this.props.assignedEntries[i].flight;
      if (flights.indexOf(flight) === -1) {
        flights.push(flight);
      }
    }
    flights.sort();
    return flights;
  };

  getSelectedFlight = (): Flight => {
    return this.isClassicLiftingWindow() ? this.props.state.lifting.flight : this.state.flight;
  };

  getActiveLift = (): Lift => {
    return this.isClassicLiftingWindow() ? this.props.state.lifting.lift : this.state.lift;
  };

  getActiveOverrideAttempt = (): number | null => {
    return this.isClassicLiftingWindow() ? this.props.state.lifting.overrideAttempt : this.state.overrideAttempt;
  };

  getActiveOverrideEntryId = (): number | null => {
    return this.isClassicLiftingWindow() ? this.props.state.lifting.overrideEntryId : this.state.overrideEntryId;
  };

  getEffectiveFlight = (flights: ReadonlyArray<Flight>): Flight => {
    const selectedFlight = this.getSelectedFlight();
    if (flights.indexOf(selectedFlight) !== -1) {
      return selectedFlight;
    }
    return flights.length > 0 ? flights[0] : selectedFlight;
  };

  makeScopedLiftingState = (platformRef: PlatformRef, flight: Flight): LiftingState => {
    return {
      ...this.props.state.lifting,
      day: platformRef.day,
      platform: platformRef.platformId,
      flight,
      lift: this.getActiveLift(),
      overrideAttempt: this.getActiveOverrideAttempt(),
      overrideEntryId: this.getActiveOverrideEntryId(),
    };
  };

  makeAttemptKey = (
    platformRef: PlatformRef,
    entryId: number | null,
    lift: Lift,
    attemptOneIndexed: number,
  ): string => {
    if (entryId === null) {
      return [platformRef.day, platformRef.platformId, "", lift, attemptOneIndexed].join(":");
    }
    return makePlatformLiveAttemptKey(platformRef, entryId, lift, attemptOneIndexed);
  };

  getJudgeVotesForAttempt = (
    platformRef: PlatformRef,
    entryId: number | null,
    lift: Lift,
    attemptOneIndexed: number,
  ): JudgeVotes => {
    const attemptKey = this.makeAttemptKey(platformRef, entryId, lift, attemptOneIndexed);
    if (this.state.judgeVotesAttemptKey !== attemptKey) {
      return {};
    }
    return this.state.judgeVotes;
  };

  handleFlightChange = (event: React.BaseSyntheticEvent) => {
    const value = event.currentTarget.value;
    if (typeof value === "string" && assertFlight(value)) {
      this.setState({
        flight: value,
        overrideEntryId: this.getEntryIdForFlight(value, this.state.overrideEntryId),
        judgeVotes: {},
        judgeVotesAttemptKey: null,
      });
    }
  };

  handleLiftChange = (event: React.BaseSyntheticEvent) => {
    const value = event.currentTarget.value;
    if (typeof value === "string" && assertLift(value)) {
      this.setState({
        lift: value,
        judgeVotes: {},
        judgeVotesAttemptKey: null,
      });
    }
  };

  handleClassicFlightChange = (event: React.BaseSyntheticEvent) => {
    const value = event.currentTarget.value;
    const platformRef = this.props.platformRef;
    if (typeof value === "string" && assertFlight(value) && platformRef !== null) {
      this.props.setLiftingGroup(platformRef.day, platformRef.platformId, value, this.props.state.lifting.lift);
      this.setState({ judgeVotes: {}, judgeVotesAttemptKey: null, awaitingNext: false, heldVerdict: null });
    }
  };

  handleClassicLiftChange = (event: React.BaseSyntheticEvent) => {
    const value = event.currentTarget.value;
    const platformRef = this.props.platformRef;
    if (typeof value === "string" && assertLift(value) && platformRef !== null) {
      this.props.setLiftingGroup(platformRef.day, platformRef.platformId, this.props.state.lifting.flight, value);
      this.setState({ judgeVotes: {}, judgeVotesAttemptKey: null, awaitingNext: false, heldVerdict: null });
    }
  };

  handleAttemptChange = (event: React.BaseSyntheticEvent, currentEntryId: number | null) => {
    const attempt = Number(event.currentTarget.value);
    this.setState({
      overrideAttempt: attempt,
      overrideEntryId: currentEntryId,
      judgeVotes: {},
      judgeVotesAttemptKey: null,
    });
  };

  handleClassicAttemptChange = (event: React.BaseSyntheticEvent, currentEntryId: number | null) => {
    const attempt = Number(event.currentTarget.value);
    this.props.overrideAttempt(attempt);
    if (currentEntryId !== null) {
      this.props.overrideEntryId(currentEntryId);
    }
    this.setState({ judgeVotes: {}, judgeVotesAttemptKey: null, awaitingNext: false, heldVerdict: null });
  };

  handleLifterChange = (event: React.BaseSyntheticEvent) => {
    const entryId = Number(event.currentTarget.value);
    const platformRef = this.props.platformRef;
    if (platformRef === null) {
      return;
    }

    const isAssigned = this.props.assignedEntries.some(
      (entry) => entry.id === entryId && isAssignedEntry(entry, platformRef),
    );
    if (!isAssigned) {
      return;
    }

    this.setState({ overrideEntryId: entryId, judgeVotes: {}, judgeVotesAttemptKey: null });
  };

  handleClassicLifterChange = (event: React.BaseSyntheticEvent) => {
    const entryId = Number(event.currentTarget.value);
    const platformRef = this.props.platformRef;
    if (platformRef === null) {
      return;
    }

    const isAssigned = this.props.assignedEntries.some(
      (entry) => entry.id === entryId && isAssignedEntry(entry, platformRef),
    );
    if (!isAssigned) {
      return;
    }

    this.props.overrideEntryId(entryId);
    this.setState({ judgeVotes: {}, judgeVotesAttemptKey: null, awaitingNext: false, heldVerdict: null });
  };

  handleEnterAttempt = (entryId: number, lift: Lift, attemptOneIndexed: number, weightKg: number) => {
    const platformRef = this.props.platformRef;
    if (platformRef === null) {
      return;
    }

    const isAssigned = this.props.assignedEntries.some(
      (entry) => entry.id === entryId && isAssignedEntry(entry, platformRef),
    );
    if (!isAssigned) {
      return;
    }

    this.props.enterAttempt(entryId, lift, attemptOneIndexed, weightKg);
  };

  getEntryIdForFlight = (flight: Flight, preferredEntryId: number | null): number | null => {
    const platformRef = this.props.platformRef;
    if (platformRef === null) {
      return null;
    }

    // Absent lifters are out of the lifting order, so they can't be the selected lifter either.
    const entriesInFlight = this.props.assignedEntries.filter(
      (entry) => entry.flight === flight && isEntryPresent(entry),
    );
    if (preferredEntryId !== null && entriesInFlight.some((entry) => entry.id === preferredEntryId)) {
      return preferredEntryId;
    }

    const lifting: LiftingState = {
      ...this.props.state.lifting,
      day: platformRef.day,
      platform: platformRef.platformId,
      flight,
      lift: this.getActiveLift(),
      overrideAttempt: this.getActiveOverrideAttempt(),
      overrideEntryId: null,
    };
    const now = getLiftingOrder(entriesInFlight, lifting);
    if (now.currentEntryId !== null) {
      return now.currentEntryId;
    }
    return entriesInFlight.length > 0 ? entriesInFlight[0].id : null;
  };

  handleMarkLift = (entryId: number | null, attemptOneIndexed: number, success: boolean) => {
    const platformRef = this.props.platformRef;
    if (platformRef === null || entryId === null || this.manualLiftLockActive) {
      return;
    }

    const isAssigned = this.props.assignedEntries.some(
      (entry) => entry.id === entryId && isAssignedEntry(entry, platformRef),
    );
    if (!isAssigned) {
      return;
    }

    const lift = this.getActiveLift();
    this.sendAttemptResolved(platformRef, entryId, lift, attemptOneIndexed, success, "manual");
    this.captureResolvedRecordMarker(platformRef, entryId, lift, attemptOneIndexed);
    this.props.markLift(entryId, lift, attemptOneIndexed, success);
    this.holdCurrentAfterVerdict(entryId, attemptOneIndexed);
    this.startManualLiftLock();
    if (this.isClassicLiftingWindow()) {
      this.setFocusAttemptInputTimeout(attemptOneIndexed);
    }
    this.setState({
      overrideAttempt: null,
      overrideEntryId: null,
      judgeVotes: {},
      judgeVotesAttemptKey: null,
      pendingVerdict: null,
      awaitingNext: this.isClassicLiftingWindow(),
      heldVerdict: this.isClassicLiftingWindow() ? { result: success, votes: this.state.judgeVotes } : null,
    });
  };

  handleJudgeVote = (
    entryId: number | null,
    attemptOneIndexed: number,
    position: JudgePosition,
    vote: JudgeVoteValue,
    fromLive = false,
  ) => {
    const platformRef = this.props.platformRef;
    if (platformRef === null || entryId === null) {
      return;
    }

    const isAssigned = this.props.assignedEntries.some(
      (entry) => entry.id === entryId && isAssignedEntry(entry, platformRef),
    );
    if (!isAssigned) {
      return;
    }

    const lift = this.getActiveLift();
    const attemptKey = this.makeAttemptKey(platformRef, entryId, lift, attemptOneIndexed);
    const currentVotes = this.state.judgeVotesAttemptKey === attemptKey ? this.state.judgeVotes : {};
    const judgeVotes = {
      ...currentVotes,
      [position]: vote,
    };
    const resolved = resolveJudgeVotes(judgeVotes);

    if (resolved === null) {
      this.setState({ judgeVotes, judgeVotesAttemptKey: attemptKey, pendingVerdict: null });
      return;
    }

    // Manual mode: hold the verdict (the operator's live vote lights stay visible; a re-vote recomputes it)
    // and wait for the operator to press "Confirmar veredicto". Do NOT resolve or write the scoresheet yet.
    if (this.isManualVerdictMode()) {
      this.setState({
        judgeVotes,
        judgeVotesAttemptKey: attemptKey,
        pendingVerdict: { attemptKey, entryId, lift, attemptOneIndexed, result: resolved },
      });
      return;
    }

    if (fromLive) {
      this.sendAttemptResolved(platformRef, entryId, lift, attemptOneIndexed, resolved, "automatic");
    }
    this.captureResolvedRecordMarker(platformRef, entryId, lift, attemptOneIndexed);
    this.props.markLift(entryId, lift, attemptOneIndexed, resolved);
    this.holdCurrentAfterVerdict(entryId, attemptOneIndexed);
    this.setState({
      overrideAttempt: null,
      overrideEntryId: null,
      judgeVotes: {},
      judgeVotesAttemptKey: null,
      pendingVerdict: null,
      awaitingNext: this.isClassicLiftingWindow(),
      heldVerdict: this.isClassicLiftingWindow() ? { result: resolved, votes: judgeVotes } : null,
    });
  };

  // Operator commits the held verdict (manual mode). Reuses the exact automatic effects
  // (attemptResolved + markLift) so the server/displays behave identically — just later.
  handleConfirmVerdict = () => {
    const pv = this.state.pendingVerdict;
    const platformRef = this.props.platformRef;
    if (pv === null || platformRef === null || this.manualLiftLockActive) {
      return;
    }
    this.sendAttemptResolved(platformRef, pv.entryId, pv.lift, pv.attemptOneIndexed, pv.result, "automatic");
    this.captureResolvedRecordMarker(platformRef, pv.entryId, pv.lift, pv.attemptOneIndexed);
    this.props.markLift(pv.entryId, pv.lift, pv.attemptOneIndexed, pv.result);
    this.holdCurrentAfterVerdict(pv.entryId, pv.attemptOneIndexed);
    this.startManualLiftLock();
    if (this.isClassicLiftingWindow()) {
      this.setFocusAttemptInputTimeout(pv.attemptOneIndexed);
    }
    this.setState({
      pendingVerdict: null,
      overrideAttempt: null,
      overrideEntryId: null,
      judgeVotes: {},
      judgeVotesAttemptKey: null,
      awaitingNext: this.isClassicLiftingWindow(),
      heldVerdict: this.isClassicLiftingWindow() ? { result: pv.result, votes: this.state.judgeVotes } : null,
    });
  };

  // Keep the just-resolved attempt SELECTED via the override so the table cell stays the active/editable
  // cell instead of flipping to green/red right at the verdict (the operator found that confusing). markLift
  // already recorded the result — the scoresheet is saved either way — so this is purely the visual hold:
  // the cell turns green/red only when the operator presses "Siguiente" (which clears the override). Classic
  // ?lifting window only (Redux override).
  holdCurrentAfterVerdict = (entryId: number | null, attemptOneIndexed: number) => {
    if (entryId === null || !this.isClassicLiftingWindow()) {
      return;
    }
    this.props.overrideEntryId(entryId);
    this.props.overrideAttempt(attemptOneIndexed);
  };

  // The scoresheet status (1 good / -1 no-lift / 0 pending) of the attempt pinned by the Redux override. The
  // override is set at the verdict (holdCurrentAfterVerdict) and cleared by "Siguiente". Redux is GLOBAL and
  // survives the operator changing screens — so the hold/button can be derived from it instead of from
  // component state (awaitingNext/heldVerdict) that is destroyed when the lifting screen unmounts.
  heldOverrideStatus = (): number => {
    const overrideEntryId = this.getActiveOverrideEntryId();
    const overrideAttempt = this.getActiveOverrideAttempt();
    if (overrideEntryId === null || overrideAttempt === null) {
      return 0;
    }
    const entry = this.props.assignedEntries.find((candidate) => candidate.id === overrideEntryId);
    if (entry === undefined) {
      return 0;
    }
    const statuses = entry[liftToStatusFieldName(this.getActiveLift())];
    return statuses[overrideAttempt - 1] ?? 0;
  };

  // Are we holding a committed verdict, waiting for the operator to press "Siguiente"? Derived primarily from
  // the Redux override pinning a RESOLVED attempt — which survives changing screens, so the "Siguiente" button
  // reliably reappears. awaitingNext is just the immediate (synchronous) signal at the moment of the verdict.
  // Classic ?lifting only. "Siguiente" clears the override → no longer holding.
  isHoldingForNext = (): boolean => {
    if (!this.isClassicLiftingWindow()) {
      return false;
    }
    return this.state.awaitingNext || this.heldOverrideStatus() !== 0;
  };

  // "Siguiente": release the manual-next hold. Clearing the override flips the just-resolved cell to
  // green/red and lets the natural order advance; clearing awaitingNext lets the held LIVE publish go out so
  // the displays move on to the next lifter. Classic ?lifting only.
  // Veredicto del intento en espera de "Siguiente": el de recién o, tras volver a montar, el de la planilla.
  isHeldVerdictGood = (): boolean => this.state.heldVerdict?.result ?? this.heldOverrideStatus() === 1;

  // Cuando entra un veredicto (por los jueces, confirmado o marcado a mano) la tanda espera "Siguiente". Si
  // la ventana no está a la vista, una notificación del sistema se lo avisa al operador.
  notifyWhenVerdictAwaitsNext = () => {
    const holding = this.isHoldingForNext();
    if (holding && !this.wasHoldingForNext) {
      const good = this.isHeldVerdictGood();
      const { currentAttempt, nextAttempt } = this.getPlatformLiveAttempts();
      const lifter = currentAttempt === null ? "" : `${currentAttempt.lifterName}. `;
      const next =
        nextAttempt === null
          ? 'Tocá "Siguiente" para seguir.'
          : `Tocá "Siguiente" para pasar a ${nextAttempt.lifterName}.`;
      this.pendingNextNotification?.close();
      this.pendingNextNotification = notifyOperator(`Veredicto: ${good ? "VÁLIDO" : "NULO"}`, `${lifter}${next}`);
    } else if (!holding && this.wasHoldingForNext) {
      this.pendingNextNotification?.close();
      this.pendingNextNotification = null;
    }
    this.wasHoldingForNext = holding;
  };

  handleNextLifter = () => {
    requestOperatorNotificationPermission();
    const platformRef = this.props.platformRef;
    if (platformRef !== null && this.isClassicLiftingWindow()) {
      this.props.setLiftingGroup(
        platformRef.day,
        platformRef.platformId,
        this.props.state.lifting.flight,
        this.getActiveLift(),
      );
    }
    this.setState({ awaitingNext: false, heldVerdict: null }, () => this.publishCurrentAttemptIfNeeded());
  };

  sendAttemptResolved = (
    platformRef: PlatformRef,
    entryId: number,
    lift: Lift,
    attemptOneIndexed: number,
    success: boolean,
    source: "automatic" | "manual",
  ) => {
    const attemptKey = makePlatformLiveAttemptKey(platformRef, entryId, lift, attemptOneIndexed);
    this.sendLive({
      type: "attemptResolved",
      day: platformRef.day,
      platformId: platformRef.platformId,
      attemptKey,
      source,
      result: success ? "good" : "noLift",
    });
  };

  captureResolvedRecordMarker = (platformRef: PlatformRef, entryId: number, lift: Lift, attemptOneIndexed: number) => {
    const attemptKey = this.makeAttemptKey(platformRef, entryId, lift, attemptOneIndexed);
    this.props.captureResolvedRecordAttempt(
      attemptKey,
      this.getRecordCategoryForEntry(entryId, lift, attemptOneIndexed),
    );
  };

  connectLive = () => {
    const platformRef = this.props.platformRef;
    if (platformRef === null || !this.props.isValidPlatform) {
      return;
    }
    if (!canUseBrowserWebSocket()) {
      this.setState({ liveStatus: "rejected", liveError: "Browser WebSocket is unavailable." });
      return;
    }

    const socket = new window.WebSocket(
      getBrowserPlatformLiveWebSocketUrl(platformRef, this.props.locationSearch, this.props.activeProfile),
    );
    this.liveSocket = socket;
    this.liveAccepted = false;
    this.lastPublishedAttemptKey = null;
    this.setState({ liveStatus: "connecting", liveError: null });

    socket.onopen = () => {
      if (this.liveSocket !== socket) {
        return;
      }
      this.sendLive({ type: "operatorJoin", day: platformRef.day, platformId: platformRef.platformId });
    };
    socket.onmessage = (event) => {
      if (this.liveSocket !== socket) {
        return;
      }
      // Un mensaje puede disparar varios cambios seguidos (el veredicto marca la planilla, fija el atleta y el
      // intento en espera). Fuera de un evento de React, React 16 renderiza después de cada uno y
      // componentDidUpdate publicaba los estados intermedios: el atleta siguiente y, peor, el intento recién
      // cerrado, que el server volvía a poner en curso (el juez central podía pedir barra lista y los votos
      // se rechazaban). Agrupados, se renderiza una sola vez con la espera ya activa.
      batch(() => this.handleLiveMessage(event.data));
    };
    socket.onclose = () => {
      if (this.liveSocket !== socket) {
        return;
      }
      this.liveSocket = null;
      this.liveAccepted = false;
      this.lastPublishedAttemptKey = null;
      this.stopClockTicker();
      this.scheduleReconnect();
    };
  };

  disconnectLive = () => {
    this.clearReconnectTimer();
    const socket = this.liveSocket;
    this.liveSocket = null;
    this.liveAccepted = false;
    this.lastPublishedAttemptKey = null;
    if (socket !== null) {
      socket.close();
    }
    if (!this.isLiveMode() && this.state.liveStatus !== "off") {
      this.setState({ liveStatus: "off", liveError: null });
    }
    this.stopClockTicker();
  };

  clearReconnectTimer = () => {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  // Auto-reconnect con backoff: el WS se cae seguido cuando el host cloud se duerme/reinicia o hay
  // un blip de red. En vez de quedar "disconnected", reintentamos solos hasta recuperar.
  scheduleReconnect = () => {
    this.clearReconnectTimer();
    if (this.state.liveStatus === "rejected") {
      return; // terminal (token inválido / ya conectado): sólo reconexión manual
    }
    if (!this.isLiveMode()) {
      this.setState({ liveStatus: "disconnected" });
      return;
    }
    const delay = Math.min(10000, 1000 * Math.pow(1.6, this.reconnectAttempts));
    this.reconnectAttempts += 1;
    this.setState({ liveStatus: "reconnecting" });
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.isLiveMode()) {
        this.connectLive();
      }
    }, delay);
  };

  reconnectNow = () => {
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    const socket = this.liveSocket;
    this.liveSocket = null;
    if (socket !== null) {
      try {
        socket.close();
      } catch (_error) {
        /* ignore */
      }
    }
    if (this.isLiveMode()) {
      this.connectLive();
    }
  };

  sendLive = (message: unknown) => {
    if (this.liveSocket === null || this.liveSocket.readyState !== 1) {
      return;
    }
    this.liveSocket.send(JSON.stringify(message));
  };

  handleLiveMessage = (rawData: unknown) => {
    let message: PlatformLiveServerMessage;
    try {
      message = JSON.parse(String(rawData)) as PlatformLiveServerMessage;
    } catch (_error) {
      return;
    }

    if (message.type === "accepted" && message.role === "operator") {
      this.liveAccepted = true;
      this.reconnectAttempts = 0;
      this.setState({ liveStatus: "connected", liveError: null });
      this.applyPlatformState(message.state);

      // Catch-up: the operator may have been on ANOTHER screen while the judges finished voting. The verdict
      // is resolved by the operator, so it was never given. On return, if the server still holds the current
      // attempt with a full set of votes (automatic mode) and we haven't recorded it yet, resolve it now — so
      // the verdict + "Siguiente" appear without the operator having to re-press Good/No Lift.
      const platformRef = this.props.platformRef;
      const pending = message.state.currentAttempt;
      if (pending !== null && platformRef !== null && this.isJudgeVotingEnabled() && !this.isManualVerdictMode()) {
        const resolved = resolveJudgeVotes(message.state.votes);
        const entry = this.props.assignedEntries.find((candidate) => candidate.id === pending.entryId);
        const alreadyMarked =
          entry !== undefined && (entry[liftToStatusFieldName(pending.lift)][pending.attempt - 1] ?? 0) !== 0;
        if (resolved !== null && entry !== undefined && !alreadyMarked) {
          this.sendAttemptResolved(platformRef, pending.entryId, pending.lift, pending.attempt, resolved, "automatic");
          this.captureResolvedRecordMarker(platformRef, pending.entryId, pending.lift, pending.attempt);
          this.props.markLift(pending.entryId, pending.lift, pending.attempt, resolved);
          this.holdCurrentAfterVerdict(pending.entryId, pending.attempt);
          this.setState({
            awaitingNext: this.isClassicLiftingWindow(),
            heldVerdict: this.isClassicLiftingWindow() ? { result: resolved, votes: message.state.votes } : null,
          });
          return;
        }
      }
      // Al reconectar (cambiar de pestaña/pantalla y volver) el server TODAVÍA sostiene el intento en
      // curso. Lo ADOPTAMOS y NO re-publicamos: re-publicar pisaría la selección del server con un intento
      // recalculado localmente (que tras re-montar puede ser distinto o null —el flight/lift/override de la
      // vista de operación son estado de componente—) y DESELECCIONARÍA al atleta en las pantallas de los
      // jueces, cortándoles el flujo. Sembramos lastPublishedAttemptKey con nuestro token calculado para
      // que un publish posterior idéntico se saltee; un cambio REAL del operador después sí publica.
      // Si el server NO tiene intento (sala fresca o tras un veredicto), publicamos nuestro intento actual.
      const serverAttempt = message.state.currentAttempt;
      if (serverAttempt !== null) {
        const { currentAttempt, nextAttempt } = this.getPlatformLiveAttempts();
        this.lastPublishedAttemptKey = [
          this.getAttemptPublicationToken(currentAttempt),
          this.getAttemptPublicationToken(nextAttempt),
        ].join("|");
      } else {
        this.publishCurrentAttemptIfNeeded();
      }
      return;
    }
    if (message.type === "rejected") {
      this.liveAccepted = false;
      this.setState({ liveStatus: "rejected", liveError: message.reason });
      return;
    }
    // Another operator screen/device took over this room (last-writer-wins on the server). Stop here —
    // terminal, no auto-reconnect — so two operator tabs don't flap by repeatedly taking over each other.
    // In the normal reconnect (changing screens) this is never delivered to the live socket: the replaced
    // connection is the OLD one, already closed, while the new one is the accepted operator.
    if (message.type === "replaced") {
      this.liveAccepted = false;
      this.setState({ liveStatus: "rejected", liveError: message.reason });
      return;
    }
    if (message.type === "platformState") {
      this.applyPlatformState(message.state);
      return;
    }
    if (message.type === "attemptClosed") {
      if (this.state.judgeVotesAttemptKey === message.attemptKey) {
        this.setState({ judgeVotes: {}, judgeVotesAttemptKey: null, awaitingNext: false, heldVerdict: null });
      }
      if (this.state.pendingVerdict?.attemptKey === message.attemptKey) {
        this.setState({ pendingVerdict: null });
      }
      return;
    }
    if (message.type === "judgeVote") {
      if (!this.isJudgeVotingEnabled()) {
        return;
      }
      const context = this.getCurrentAttemptContext();
      if (context === null || context.currentEntryId === null) {
        return;
      }
      const platformRef = this.props.platformRef;
      if (platformRef === null) {
        return;
      }
      const attemptKey = this.makeAttemptKey(
        platformRef,
        context.currentEntryId,
        this.getActiveLift(),
        context.attemptOneIndexed,
      );
      if (attemptKey !== message.attemptKey) {
        return;
      }
      this.handleJudgeVote(
        context.currentEntryId,
        context.attemptOneIndexed,
        message.judgePosition,
        message.vote,
        true,
      );
    }
  };

  applyPlatformState = (platformState: PlatformLiveState) => {
    const receivedAtMs = Date.now();
    const nextState: Pick<
      InternalState,
      "attemptClock" | "serverNowMs" | "clockReceivedAtMs" | "clockTick" | "dmxStatus"
    > &
      Partial<Pick<InternalState, "judgeVotes" | "judgeVotesAttemptKey" | "pendingVerdict">> = {
      attemptClock: platformState.attemptClock || null,
      serverNowMs: typeof platformState.serverNowMs === "number" ? platformState.serverNowMs : receivedAtMs,
      clockReceivedAtMs: receivedAtMs,
      clockTick: receivedAtMs,
      dmxStatus: platformState.dmx?.status || "disabled",
    };

    const current = platformState.currentAttempt;
    if (current !== null) {
      nextState.judgeVotes = platformState.votes;
      nextState.judgeVotesAttemptKey = current.key;
      // Re-derive a held verdict after an operator reconnect (pendingVerdict is local-only): if manual
      // mode and all three votes are already in on the server, re-show the "Confirmar" button.
      if (this.isManualVerdictMode()) {
        const resolved = resolveJudgeVotes(platformState.votes);
        nextState.pendingVerdict =
          resolved === null
            ? null
            : {
                attemptKey: current.key,
                entryId: current.entryId,
                lift: current.lift,
                attemptOneIndexed: current.attempt,
                result: resolved,
              };
      }
    } else {
      nextState.judgeVotes = {};
      nextState.judgeVotesAttemptKey = null;
      nextState.pendingVerdict = null;
    }

    this.setState(
      nextState as Pick<
        InternalState,
        | "attemptClock"
        | "serverNowMs"
        | "clockReceivedAtMs"
        | "clockTick"
        | "judgeVotes"
        | "judgeVotesAttemptKey"
        | "pendingVerdict"
      >,
    );

    if (platformState.attemptClock !== null && platformState.attemptClock !== undefined) {
      this.startClockTicker();
    } else {
      this.stopClockTicker();
    }
  };

  startClockTicker = () => {
    if (this.clockTimer !== null) {
      return;
    }
    this.clockTimer = window.setInterval(() => {
      this.setState({ clockTick: Date.now() });
    }, 1_000);
  };

  stopClockTicker = () => {
    if (this.clockTimer === null) {
      return;
    }
    window.clearInterval(this.clockTimer);
    this.clockTimer = null;
  };

  clearManualLiftLockTimer = () => {
    if (this.manualLiftLockTimer === null) {
      return;
    }
    window.clearTimeout(this.manualLiftLockTimer);
    this.manualLiftLockTimer = null;
    this.manualLiftLockActive = false;
  };

  startManualLiftLock = () => {
    this.clearManualLiftLockTimer();
    this.manualLiftLockActive = true;
    this.setState({ manualLiftLocked: true });
    this.manualLiftLockTimer = window.setTimeout(() => {
      this.manualLiftLockTimer = null;
      this.manualLiftLockActive = false;
      this.setState({ manualLiftLocked: false });
    }, MANUAL_LIFT_LOCK_MS);
  };

  setFocusAttemptInputTimeout = (attemptOneIndexed: number) => {
    // Oculta (el operador está en otra pantalla): no robarle el foco a lo que esté escribiendo.
    if (!this.isFullPresentation() || globalFocusAttemptInputId === null || attemptOneIndexed >= 3) {
      return;
    }

    const attemptInputId = globalFocusAttemptInputId;
    window.setTimeout(() => {
      const elem = document.getElementById(attemptInputId);
      if (elem) {
        elem.focus();
      }
    }, 100);
  };

  handleOnMouseEnter = () => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  };

  getCurrentClockSeconds = (attemptKey: string): number | null => {
    if (this.state.attemptClock?.attemptKey !== attemptKey) {
      return null;
    }

    return getRemainingClockSeconds(
      {
        attemptClock: this.state.attemptClock,
        serverNowMs: this.state.serverNowMs,
        receivedAtMs: this.state.clockReceivedAtMs,
      },
      this.state.clockTick,
    );
  };

  renderAttemptClock = (platformRef: PlatformRef, currentEntryId: number | null, attemptOneIndexed: number) => {
    if (currentEntryId === null) {
      return null;
    }

    const attemptKey = this.makeAttemptKey(platformRef, currentEntryId, this.getActiveLift(), attemptOneIndexed);
    const remainingSeconds = this.getCurrentClockSeconds(attemptKey);
    if (remainingSeconds === null) {
      return null;
    }

    return (
      <div className={styles.attemptClock} aria-label="Attempt Clock">
        Tiempo: {remainingSeconds}s
      </div>
    );
  };

  getCurrentAttemptContext = (): CurrentAttemptContext | null => {
    const platformRef = this.props.platformRef;
    if (platformRef === null || !this.props.isValidPlatform) {
      return null;
    }

    const flights = this.getFlights();
    const effectiveFlight = this.getEffectiveFlight(flights);
    const entriesInFlight = this.props.assignedEntries.filter((entry) => entry.flight === effectiveFlight);
    const lifting = this.makeScopedLiftingState(platformRef, effectiveFlight);
    const now = getLiftingOrder(entriesInFlight, lifting);
    const currentEntry =
      now.currentEntryId === null ? null : entriesInFlight.find((entry) => entry.id === now.currentEntryId) || null;

    return {
      effectiveFlight,
      entriesInFlight,
      orderedEntries: now.orderedEntries,
      currentEntry,
      currentEntryId: now.currentEntryId,
      attemptOneIndexed: now.attemptOneIndexed,
      nextEntry:
        now.nextEntryId === null ? null : entriesInFlight.find((entry) => entry.id === now.nextEntryId) || null,
      nextEntryId: now.nextEntryId,
      nextAttemptOneIndexed: now.nextAttemptOneIndexed,
    };
  };

  makePlatformLiveAttempt = (
    platformRef: PlatformRef,
    entry: Entry | null,
    entryId: number | null,
    attemptOneIndexed: number | null,
  ): PlatformLiveAttempt | null => {
    if (entry === null || entryId === null || attemptOneIndexed === null) {
      return null;
    }

    const lift = this.getActiveLift();
    const fieldKg = liftToAttemptFieldName(lift);
    const weightKg = entry[fieldKg][attemptOneIndexed - 1];
    const barAndCollarsWeightKg = this.getBarAndCollarsWeightKgForLift(lift);
    const anchor = this.props.state.wrpfSession.entryAnchors.find(
      (candidate) => candidate.openlifterEntryId === entry.id,
    );
    const hasLocation = Boolean(entry.city || entry.state || entry.country);
    const hasPersonalBests =
      entry.personalBestSquatKg !== undefined ||
      entry.personalBestBenchKg !== undefined ||
      entry.personalBestDeadliftKg !== undefined;
    const weightClassKg = getEntryWeightClassLabel(entry, this.props.state.meet, this.props.state.language);
    const entryEvents = entry.events;
    const key = makePlatformLiveAttemptKey(platformRef, entryId, lift, attemptOneIndexed);
    const recordCategory = this.getRecordCategoryForEntry(entryId, lift, attemptOneIndexed);
    const recordCategoryFor: RecordCategoryLookup = (seriesLift, seriesAttempt) =>
      this.getRecordCategoryForEntry(entryId, seriesLift, seriesAttempt);
    return {
      key,
      day: platformRef.day,
      platformId: platformRef.platformId,
      entryId,
      lift,
      attempt: attemptOneIndexed,
      lifterName: entry.name,
      weightKg,
      flight: entry.flight,
      ...(recordCategory === undefined ? {} : { recordAttempt: true, recordCategory }),
      athleteMeta: {
        team: entry.team,
        lift,
        category: entry.divisions.length > 0 ? entry.divisions.join(", ") : entry.intendedWeightClassKg,
        age: entry.age,
        ageDivision: entry.divisions[0] || "",
        ...(entry.bodyweightKg > 0 ? { bodyweightKg: entry.bodyweightKg } : {}),
        equipment: entry.equipment,
        weightClassKg,
        ...(entryEvents.length > 0 ? { events: [...entryEvents] } : {}),
        ...(entry.instagram ? { instagram: entry.instagram } : {}),
        ...(entry.photoUrl ? { photoUrl: entry.photoUrl } : {}),
        ...(hasLocation
          ? { location: { city: entry.city, state: entry.state || undefined, country: entry.country || undefined } }
          : {}),
        primaryClassification: {
          equipment: entry.equipment,
          ...(anchor
            ? {
                event: anchor.primaryEvent,
                division: anchor.primaryDivision,
                weightClass: anchor.weightClass,
              }
            : {}),
        },
        ...(hasPersonalBests
          ? {
              personalBestsKg: {
                squat: entry.personalBestSquatKg,
                bench: entry.personalBestBenchKg,
                deadlift: entry.personalBestDeadliftKg,
              },
            }
          : {}),
      },
      attemptSeries: buildAttemptSeries(entry, lift, recordCategoryFor, attemptOneIndexed),
      liftSeries: buildLiftSeries(entry, lift, attemptOneIndexed, recordCategoryFor),
      barLoad: {
        loading: selectPlates(
          weightKg,
          barAndCollarsWeightKg,
          this.props.state.meet.plates,
          this.props.state.meet.inKg,
        ),
        inKg: this.props.state.meet.inKg,
        rackInfo: this.getRackInfoForLift(entry, lift),
      },
    };
  };

  getBarAndCollarsWeightKgForLift = (lift: Lift): number => {
    if (lift === "S") {
      return this.props.state.meet.squatBarAndCollarsWeightKg;
    }
    if (lift === "B") {
      return this.props.state.meet.benchBarAndCollarsWeightKg;
    }
    return this.props.state.meet.deadliftBarAndCollarsWeightKg;
  };

  getRackInfoForLift = (entry: Entry, lift: Lift): string => {
    if (lift === "S") {
      return entry.squatRackInfo;
    }
    if (lift === "B") {
      return entry.benchRackInfo;
    }
    return "";
  };

  // Nombre del torneo y sponsors para overlay y ficha: las PCs del stream no tienen el meet cargado.
  getBroadcastInfo = (): PlatformLiveBroadcastInfo => {
    const meetName = this.props.state.meet.name.trim();
    const sponsors = this.props.state.wrpfSession.sponsors ?? [];
    return {
      ...(meetName === "" ? {} : { meetName }),
      ...(sponsors.length === 0 ? {} : { sponsors: sponsors.map((sponsor) => ({ ...sponsor })) }),
    };
  };

  getPlatformLiveAttempts = (): {
    currentAttempt: PlatformLiveAttempt | null;
    nextAttempt: PlatformLiveAttempt | null;
  } => {
    const platformRef = this.props.platformRef;
    const context = this.getCurrentAttemptContext();
    if (platformRef === null || context === null) {
      return { currentAttempt: null, nextAttempt: null };
    }

    const currentAttempt = this.makePlatformLiveAttempt(
      platformRef,
      context.currentEntry,
      context.currentEntryId,
      context.attemptOneIndexed,
    );
    return {
      currentAttempt: currentAttempt === null ? null : { ...currentAttempt, broadcast: this.getBroadcastInfo() },
      nextAttempt: this.makePlatformLiveAttempt(
        platformRef,
        context.nextEntry,
        context.nextEntryId,
        context.nextAttemptOneIndexed,
      ),
    };
  };

  publishCurrentAttemptIfNeeded = () => {
    const platformRef = this.props.platformRef;
    if (!this.liveAccepted || platformRef === null) {
      return;
    }

    // Manual-next hold: a verdict was committed but the operator hasn't pressed "Siguiente" yet. Don't publish
    // the advanced attempt — that's exactly what "Siguiente" releases. Derived from the Redux override (not
    // just component state) so the hold also holds after the operator changes screens and reconnects.
    if (this.isHoldingForNext()) {
      return;
    }

    const { currentAttempt, nextAttempt } = this.getPlatformLiveAttempts();
    const publicationKey = [
      this.getAttemptPublicationToken(currentAttempt),
      this.getAttemptPublicationToken(nextAttempt),
    ].join("|");
    if (publicationKey === this.lastPublishedAttemptKey) {
      return;
    }

    this.lastPublishedAttemptKey = publicationKey;
    this.sendLive({
      type: "currentAttempt",
      day: platformRef.day,
      platformId: platformRef.platformId,
      attempt: currentAttempt,
      nextAttempt,
    });
  };

  getAttemptPublicationToken = (attempt: PlatformLiveAttempt | null): string => {
    return JSON.stringify(attempt);
  };

  getRecordSuggestionCategory = (
    entry: Entry | null,
    attemptOneIndexed: number,
    lift: Lift,
  ): WrpfRecordCategory | undefined => {
    const categories = this.getRecordSuggestions(entry, attemptOneIndexed, lift)
      .map((suggestion) => recordCategoryFromJurisdiction(suggestion.jurisdiction))
      .filter((category): category is WrpfRecordCategory => category !== undefined);
    return highestRecordCategory(categories);
  };

  getRecordCategoryForEntry = (
    entryId: number,
    lift: Lift,
    attemptOneIndexed: number,
  ): WrpfRecordCategory | undefined => {
    const platformRef = this.props.platformRef;
    if (platformRef === null) {
      return undefined;
    }
    const attemptKey = this.makeAttemptKey(platformRef, entryId, lift, attemptOneIndexed);
    const entry = this.props.assignedEntries.find((candidate) => candidate.id === entryId) ?? null;
    const runtime = this.props.state.wrpfLive.recordMarking;
    const suggestionCategory = this.getRecordSuggestionCategory(entry, attemptOneIndexed, lift);
    return resolveRecordCategory({
      mode: runtime.mode,
      override: runtime.overrides[attemptKey],
      categoryWhenResolved: runtime.categoriesWhenResolved[attemptKey],
      suggestionCategories: suggestionCategory === undefined ? [] : [suggestionCategory],
    });
  };

  isRecordAttemptForEntry = (entryId: number, lift: Lift, attemptOneIndexed: number): boolean =>
    this.getRecordCategoryForEntry(entryId, lift, attemptOneIndexed) !== undefined;

  getRecordCandidateCategoryForEntry = (
    entryId: number,
    lift: Lift,
    attemptOneIndexed: number,
  ): WrpfRecordCategory | undefined => {
    const entry = this.props.assignedEntries.find((candidate) => candidate.id === entryId) ?? null;
    return this.getRecordSuggestionCategory(entry, attemptOneIndexed, lift);
  };

  isRecordCandidateForEntry = (entryId: number, lift: Lift, attemptOneIndexed: number): boolean => {
    const entry = this.props.assignedEntries.find((candidate) => candidate.id === entryId) ?? null;
    return this.getRecordSuggestions(entry, attemptOneIndexed, lift).length > 0;
  };

  toggleRecordAttempt = (
    attemptKey: string | null,
    currentCategory: WrpfRecordCategory | undefined,
    inferredCategory: WrpfRecordCategory | undefined,
  ) => {
    if (attemptKey === null) {
      return;
    }
    if (currentCategory !== undefined) {
      this.props.setRecordAttemptOverride(attemptKey, false);
    } else if (inferredCategory !== undefined) {
      this.props.setRecordAttemptOverride(attemptKey, inferredCategory);
    }
  };

  handleDocumentMouseDown = (event: MouseEvent) => {
    if (this.state.recordContextMenu === null) {
      return;
    }

    const target = event.target;
    if (target instanceof Node && this.recordContextMenuRef?.contains(target) === true) {
      return;
    }

    this.setState({ recordContextMenu: null });
  };

  handleAttemptRecordContextMenu = (
    entryId: number,
    lift: Lift,
    attemptOneIndexed: number,
    event: React.MouseEvent<HTMLTableCellElement>,
  ) => {
    if (!this.isLiveMode()) {
      return;
    }

    event.preventDefault();
    this.setState({
      recordContextMenu: {
        entryId,
        lift,
        attemptOneIndexed,
        x: event.clientX,
        y: event.clientY,
        categorySubmenuOpen: false,
      },
    });
  };

  handleRecordContextMenuSubmenu = () => {
    this.setState((state) => ({
      recordContextMenu:
        state.recordContextMenu === null ? null : { ...state.recordContextMenu, categorySubmenuOpen: true },
    }));
  };

  handleRecordContextMenuToggle = () => {
    const menu = this.state.recordContextMenu;
    const platformRef = this.props.platformRef;
    if (menu === null || platformRef === null) {
      return;
    }

    const attemptKey = this.makeAttemptKey(platformRef, menu.entryId, menu.lift, menu.attemptOneIndexed);
    const entry = this.props.assignedEntries.find((candidate) => candidate.id === menu.entryId) ?? null;
    this.toggleRecordAttempt(
      attemptKey,
      this.getRecordCategoryForEntry(menu.entryId, menu.lift, menu.attemptOneIndexed),
      this.getRecordSuggestionCategory(entry, menu.attemptOneIndexed, menu.lift),
    );
    this.setState({ recordContextMenu: null });
  };

  handleRecordContextMenuCategory = (category: WrpfRecordCategory) => {
    const menu = this.state.recordContextMenu;
    const platformRef = this.props.platformRef;
    if (menu === null || platformRef === null) return;
    const attemptKey = this.makeAttemptKey(platformRef, menu.entryId, menu.lift, menu.attemptOneIndexed);
    this.props.setRecordAttemptOverride(attemptKey, category);
    this.setState({ recordContextMenu: null });
  };

  handleRecordContextMenuReset = () => {
    const menu = this.state.recordContextMenu;
    const platformRef = this.props.platformRef;
    if (menu === null || platformRef === null) {
      return;
    }

    const attemptKey = this.makeAttemptKey(platformRef, menu.entryId, menu.lift, menu.attemptOneIndexed);
    this.props.clearRecordAttemptOverride(attemptKey);
    this.setState({ recordContextMenu: null });
  };

  renderRecordContextMenu = () => {
    const menu = this.state.recordContextMenu;
    if (menu === null) {
      return null;
    }

    const currentCategory = this.getRecordCategoryForEntry(menu.entryId, menu.lift, menu.attemptOneIndexed);
    const marked = currentCategory !== undefined;
    const entry = this.props.assignedEntries.find((candidate) => candidate.id === menu.entryId) ?? null;
    const suggestions = this.getRecordSuggestions(entry, menu.attemptOneIndexed, menu.lift);
    const suggestedCategory = this.getRecordSuggestionCategory(entry, menu.attemptOneIndexed, menu.lift);
    const tournamentRecordCategories = WRPF_RECORD_CATEGORY_PRIORITY.filter((category) =>
      selectEnabledRecordCategories(this.props.state.wrpfRecords).includes(category),
    );
    const platformRef = this.props.platformRef;
    const attemptKey =
      platformRef === null ? null : this.makeAttemptKey(platformRef, menu.entryId, menu.lift, menu.attemptOneIndexed);
    const canResetAutomaticDecision =
      this.props.state.wrpfLive.recordMarking.mode === "automatic" &&
      attemptKey !== null &&
      this.props.state.wrpfLive.recordMarking.overrides[attemptKey] !== undefined;
    return (
      <div
        ref={(element) => {
          this.recordContextMenuRef = element;
        }}
        className={styles.recordContextMenu}
        style={{ top: menu.y, left: menu.x }}
        role="menu"
        aria-label="Menú de intento récord"
      >
        {suggestions.length > 0 ? (
          <div className={styles.recordContextRecord} aria-label="Información del récord a batir">
            {suggestions.map((suggestion) => (
              <div
                key={`${suggestion.jurisdiction}:${suggestion.discipline}:${suggestion.division}:${suggestion.liftType}`}
                className={styles.recordContextRecordDetails}
                {...recordCategoryDataAttributes(recordCategoryFromJurisdiction(suggestion.jurisdiction))}
              >
                <span>Ejercicio: {recordLiftLabel(suggestion.liftType)}</span>
                {/* Un doble inscripto tiene un récord por división (y por disciplina): sin esto se ven iguales. */}
                {suggestion.discipline === "SBD" ? null : (
                  <span>Disciplina: {recordFieldLabel("discipline", suggestion.discipline)}</span>
                )}
                <span>División: {recordFieldLabel("division", suggestion.division)}</span>
                <span>Categoría: {recordFieldLabel("weightClass", suggestion.weightClass)}</span>
                <span>Peso: {suggestion.currentKg} kg</span>
                <span>Tipo de récord: {recordJurisdictionLabel(suggestion.jurisdiction)}</span>
              </div>
            ))}
          </div>
        ) : null}
        {marked ? (
          <button
            type="button"
            role="menuitem"
            className={styles.recordContextMenuRemove}
            onClick={this.handleRecordContextMenuToggle}
          >
            Quitar marca de récord
          </button>
        ) : suggestions.length > 0 ? (
          <div className={styles.recordContextMenuSubmenuTrigger}>
            <button
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={menu.categorySubmenuOpen}
              onClick={this.handleRecordContextMenuSubmenu}
            >
              <span>Marcar como récord</span>
              <span className={styles.recordContextMenuChevron} aria-hidden="true">
                &gt;
              </span>
            </button>
            {menu.categorySubmenuOpen ? (
              <div className={styles.recordContextSubmenu} role="menu" aria-label="Elegir tipo de récord">
                {tournamentRecordCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    role="menuitemradio"
                    aria-checked={category === suggestedCategory}
                    {...recordCategoryDataAttributes(category)}
                    onClick={() => this.handleRecordContextMenuCategory(category)}
                  >
                    {recordAttemptLabel(category)}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          tournamentRecordCategories.map((category) => (
            <button
              key={category}
              type="button"
              role="menuitem"
              {...recordCategoryDataAttributes(category)}
              onClick={() => this.handleRecordContextMenuCategory(category)}
            >
              {recordAttemptLabel(category)}
            </button>
          ))
        )}
        {canResetAutomaticDecision ? (
          <button type="button" role="menuitem" onClick={this.handleRecordContextMenuReset}>
            Restablecer decisión automática
          </button>
        ) : null}
      </div>
    );
  };

  renderTableRecordBadge = (entryId: number, lift: Lift, attemptOneIndexed: number) => {
    const category = this.getRecordCategoryForEntry(entryId, lift, attemptOneIndexed);
    if (category === undefined) {
      return null;
    }
    const label = recordAttemptLabel(category);
    return (
      <span
        className={styles.tableRecordBadge}
        aria-label={recordAttemptAriaLabel(category)}
        title={label}
        {...recordCategoryDataAttributes(category)}
      >
        R
      </span>
    );
  };

  renderRecordToggle = (attempt: PlatformLiveAttempt | null, scopeLabel: "Actual" | "Próximo", ariaLabel: string) => {
    if (attempt === null) {
      return null;
    }

    const active = attempt.recordAttempt === true;
    const inferredCategory = this.getRecordCandidateCategoryForEntry(attempt.entryId, attempt.lift, attempt.attempt);
    const category = attempt.recordCategory ?? inferredCategory;
    const recordLabel = recordAttemptLabel(category);
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        aria-pressed={active}
        className={active ? styles.recordToggleActive : styles.recordToggle}
        disabled={!active && inferredCategory === undefined}
        title={!active && inferredCategory === undefined ? "Elegí la categoría desde el menú contextual" : undefined}
        onClick={() => this.toggleRecordAttempt(attempt.key, attempt.recordCategory, inferredCategory)}
        {...recordCategoryDataAttributes(category)}
      >
        {recordLabel}
        {active ? " ✓" : ""} · {scopeLabel}
      </button>
    );
  };

  getRecordSuggestions = (
    entry: Entry | null,
    attemptOneIndexed: number,
    lift: Lift = this.getActiveLift(),
  ): RecordSuggestion[] => {
    const recordCategories = selectEnabledRecordCategories(this.props.state.wrpfRecords);
    if (entry === null || recordCategories.length === 0) {
      return [];
    }
    const anchor = this.props.state.wrpfSession.entryAnchors.find(
      (candidate) => candidate.openlifterEntryId === entry.id,
    );
    const assignedEntryIds = new Set(this.props.assignedEntries.map((candidate) => candidate.id));
    return evaluateRecordAttempt({
      entry,
      anchor,
      lift,
      attemptOneIndexed,
      recordCategories,
      currentRecords: this.props.state.wrpfRecords.currentRecords,
      localEntries: this.props.assignedEntries,
      localAnchors: this.props.state.wrpfSession.entryAnchors.filter((candidate) =>
        assignedEntryIds.has(candidate.openlifterEntryId),
      ),
    });
  };

  handleFullscreen = () => {
    const element = document.getElementById(this.isClassicLiftingWindow() ? "liftingView" : "wrpfOperationLayer");
    if (element === null) {
      return;
    }

    const doc: any = document;
    const elem: any = element;
    if (doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullscreenElement || doc.msFullscreenElement) {
      if (typeof doc.exitFullscreen === "function") doc.exitFullscreen();
      else if (typeof doc.webkitExitFullscreen === "function") doc.webkitExitFullscreen();
      else if (typeof doc.mozExitFullscreen === "function") doc.mozExitFullscreen();
      else if (typeof doc.msExitFullscreen === "function") doc.msExitFullscreen();
    } else {
      if (typeof elem.requestFullscreen === "function") elem.requestFullscreen();
      else if (typeof elem.webkitRequestFullscreen === "function") elem.webkitRequestFullscreen();
      else if (typeof elem.mozRequestFullscreen === "function") elem.mozRequestFullscreen();
      else if (typeof elem.msRequestFullscreen === "function") elem.msRequestFullscreen();
    }
  };

  renderInvalid = () => {
    return (
      <div className={styles.operationLayer}>
        <div className={styles.invalid} role="alert">
          <h1>Invalid Platform Instance</h1>
          <p>The requested day and platform do not exist in this meet setup.</p>
        </div>
      </div>
    );
  };

  renderTable = (orderedEntries: Array<Entry>, currentEntryId: number | null) => {
    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.nameCell}>Lifter</th>
            <th>Flight</th>
            {LIFTS.map((lift) =>
              [1, 2, 3].map((attempt) => (
                <th key={`${lift}${attempt}`}>
                  {lift}
                  {attempt}
                </th>
              )),
            )}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {orderedEntries.map((entry) => {
            const isCurrent = entry.id === currentEntryId;
            return (
              <tr key={entry.id} className={isCurrent ? styles.currentRow : ""}>
                <td className={styles.nameCell}>{entry.name}</td>
                <td>{entry.flight}</td>
                {LIFTS.map((lift) => [1, 2, 3].map((attempt) => this.renderAttemptCell(entry, lift, attempt)))}
                <td>{this.renderTotal(entry)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  renderAttemptCell = (entry: Entry, lift: Lift, attemptOneIndexed: number) => {
    const fieldStatus = liftToStatusFieldName(lift);
    const status = entry[fieldStatus][attemptOneIndexed - 1];
    const className = status === 1 ? styles.goodLift : status === -1 ? styles.noLift : undefined;
    const recordBadge = this.renderTableRecordBadge(entry.id, lift, attemptOneIndexed);
    const recordCategory = this.getRecordCategoryForEntry(entry.id, lift, attemptOneIndexed);
    const recordCellClass = recordBadge === null ? "" : ` ${styles.attemptCellRecord}`;
    const canMarkRecordFromContextMenu = this.isLiveMode() && status === 0;

    return (
      <td
        key={`${entry.id}-${lift}${attemptOneIndexed}`}
        className={`${className || ""}${recordCellClass}`.trim() || undefined}
        {...recordCategoryDataAttributes(recordCategory)}
        onContextMenu={
          canMarkRecordFromContextMenu
            ? (event) => this.handleAttemptRecordContextMenu(entry.id, lift, attemptOneIndexed, event)
            : undefined
        }
      >
        <OperationAttemptInput
          entry={entry}
          lift={lift}
          attemptOneIndexed={attemptOneIndexed}
          inKg={this.props.state.meet.inKg}
          language={this.props.state.language}
          onEnterAttempt={this.handleEnterAttempt}
        />
        {recordBadge}
      </td>
    );
  };

  renderTotal = (entry: Entry): string => {
    const best = (lift: Lift) => {
      const fieldKg = liftToAttemptFieldName(lift);
      const fieldStatus = liftToStatusFieldName(lift);
      let bestKg = 0;
      for (let i = 0; i < 3; i++) {
        if (entry[fieldStatus][i] === 1) {
          bestKg = Math.max(bestKg, entry[fieldKg][i]);
        }
      }
      return bestKg;
    };

    const total = best("S") + best("B") + best("D");
    return total === 0 ? "" : displayWeightOnePlace(total, this.props.state.language);
  };

  renderBarLoad = (currentEntry: Entry | null, attemptOneIndexed: number) => {
    const lift = this.getActiveLift();
    const fieldKg = liftToAttemptFieldName(lift);
    const currentWeightKg = currentEntry === null ? 0 : currentEntry[fieldKg][attemptOneIndexed - 1];
    const barAndCollarsWeightKg =
      lift === "S"
        ? this.props.state.meet.squatBarAndCollarsWeightKg
        : lift === "B"
          ? this.props.state.meet.benchBarAndCollarsWeightKg
          : this.props.state.meet.deadliftBarAndCollarsWeightKg;
    const loading = selectPlates(
      currentWeightKg,
      barAndCollarsWeightKg,
      this.props.state.meet.plates,
      this.props.state.meet.inKg,
    );
    const weightKgText = displayWeightOnePlace(currentWeightKg, this.props.state.language);
    const weightLbsText = displayWeightOnePlace(kg2lbs(currentWeightKg), this.props.state.language);
    const recordCategory =
      currentEntry === null ? undefined : this.getRecordCategoryForEntry(currentEntry.id, lift, attemptOneIndexed);
    const isRecord = recordCategory !== undefined;

    const rackInfo = currentEntry === null ? "" : this.getRackInfoForLift(currentEntry, lift);

    return (
      <div
        className={isRecord ? `${styles.barLoadCard} ${styles.barLoadCardRecord}` : styles.barLoadCard}
        {...recordCategoryDataAttributes(recordCategory)}
      >
        <div className={styles.barTitle}>
          Bar Load: {weightKgText} kg / {weightLbsText} lb
          {isRecord ? (
            <span
              className={styles.barLoadRecordBadge}
              aria-label={recordAttemptAriaLabel(recordCategory)}
              {...recordCategoryDataAttributes(recordCategory)}
            >
              {recordAttemptLabel(recordCategory)}
            </span>
          ) : null}
        </div>
        <div className={styles.barArea}>
          <BarLoad loading={loading} rackInfo={rackInfo} inKg={this.props.state.meet.inKg} />
        </div>
      </div>
    );
  };

  renderJudgeVoting = (platformRef: PlatformRef, currentEntryId: number | null, attemptOneIndexed: number) => {
    if (this.isLiveMode()) {
      if (!this.isJudgeVotingEnabled()) {
        return null;
      }

      const judgeVotes = this.getJudgeVotesForAttempt(
        platformRef,
        currentEntryId,
        this.getActiveLift(),
        attemptOneIndexed,
      );
      const voteCount = JUDGE_POSITIONS.filter((position) => judgeVotes[position] !== undefined).length;

      return (
        <div className={styles.judgeVoting}>
          <div className={styles.judgeVotingHeader}>
            <h2>Judge Voting (live)</h2>
            <div className={styles.voteCount}>Votes: {voteCount}/3</div>
          </div>
          <LiveConnectionIndicators
            liveStatus={this.state.liveStatus}
            liveError={this.state.liveError}
            dmxStatus={this.state.dmxStatus}
            onReconnect={this.reconnectNow}
            theme="light"
          />
        </div>
      );
    }

    if (!this.isSimulatorMode()) {
      return (
        <div className={styles.judgeVoting}>
          <div className={styles.judgeVotingHeader}>
            <h2>Judge Voting</h2>
            <div className={styles.voteCount}>Offline</div>
          </div>
        </div>
      );
    }

    const judgeVotes = this.getJudgeVotesForAttempt(
      platformRef,
      currentEntryId,
      this.getActiveLift(),
      attemptOneIndexed,
    );
    const voteCount = JUDGE_POSITIONS.filter((position) => judgeVotes[position] !== undefined).length;

    return (
      <div className={styles.judgeVoting}>
        <div className={styles.judgeVotingHeader}>
          <h2>Judge Voting (local simulator)</h2>
          <div className={styles.voteCount}>Votes: {voteCount}/3</div>
        </div>
        <div className={styles.judgeGrid}>
          {JUDGE_POSITIONS.map((position) => {
            const vote = judgeVotes[position];
            const voteText = vote === "good" ? "Good Lift" : vote === "noLift" ? "No Lift" : "Pending";
            return (
              <div key={position} className={styles.judgeCard}>
                <div className={styles.judgePosition}>{position}</div>
                <div className={styles.judgeVoteStatus}>{voteText}</div>
                <div className={styles.judgeButtons}>
                  <button
                    type="button"
                    aria-label={`${position} Good Lift`}
                    className={styles.goodLiftButton}
                    onClick={() => this.handleJudgeVote(currentEntryId, attemptOneIndexed, position, "good")}
                  >
                    Good Lift
                  </button>
                  <button
                    type="button"
                    aria-label={`${position} No Lift`}
                    className={styles.noLiftButton}
                    onClick={() => this.handleJudgeVote(currentEntryId, attemptOneIndexed, position, "noLift")}
                  >
                    No Lift
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Manual-verdict mode: the "Confirmar veredicto" control, shown only while a verdict
  // for the CURRENT attempt is held awaiting the operator. Gated on the attempt key so a stale held
  // verdict can never render against a different attempt.
  renderPendingVerdictControls = (
    platformRef: PlatformRef,
    currentEntryId: number | null,
    attemptOneIndexed: number,
    variant: "operation" | "classic",
  ) => {
    const pv = this.state.pendingVerdict;
    if (pv === null || currentEntryId === null) {
      return null;
    }
    const currentAttemptKey = this.makeAttemptKey(platformRef, currentEntryId, this.getActiveLift(), attemptOneIndexed);
    if (pv.attemptKey !== currentAttemptKey) {
      return null;
    }
    const confirmClass =
      variant === "classic"
        ? pv.result
          ? liftingFooterStyles.goodLift
          : liftingFooterStyles.noLift
        : pv.result
          ? styles.goodLiftButton
          : styles.noLiftButton;
    return (
      <>
        <button
          type="button"
          aria-label="Confirmar veredicto"
          className={confirmClass}
          disabled={this.state.manualLiftLocked}
          onClick={this.handleConfirmVerdict}
        >
          Confirmar: {pv.result ? "VÁLIDO" : "NULO"}
        </button>
      </>
    );
  };

  renderFooter = (
    flights: Array<Flight>,
    effectiveFlight: Flight,
    currentEntryId: number | null,
    orderedEntries: Array<Entry>,
    attemptOneIndexed: number,
    platformRef: PlatformRef,
  ) => {
    const selectedEntry = currentEntryId === null ? "" : String(currentEntryId);
    const currentAttemptKey =
      currentEntryId === null
        ? null
        : this.makeAttemptKey(platformRef, currentEntryId, this.getActiveLift(), attemptOneIndexed);
    const barReadyLabel = this.getBarReadyButtonLabel(currentAttemptKey);
    const { currentAttempt, nextAttempt } = this.getPlatformLiveAttempts();
    return (
      <div className={styles.footer}>
        <div className={styles.controlGroup}>
          <Form.Control
            aria-label="Lift"
            as="select"
            value={this.state.lift}
            onChange={this.handleLiftChange}
            className={`custom-select ${styles.selector}`}
          >
            <option value="S">Squat</option>
            <option value="B">Bench</option>
            <option value="D">Deadlift</option>
          </Form.Control>
          <Form.Control
            aria-label="Flight"
            as="select"
            value={effectiveFlight}
            onChange={this.handleFlightChange}
            className={`custom-select ${styles.selector}`}
          >
            {flights.length === 0 ? <option value={effectiveFlight}>No flights</option> : null}
            {flights.map((flight) => (
              <option key={flight} value={flight}>
                Flight {flight}
              </option>
            ))}
          </Form.Control>
          <Form.Control
            aria-label="Attempt"
            as="select"
            value={attemptOneIndexed.toString()}
            onChange={(event: React.BaseSyntheticEvent) => this.handleAttemptChange(event, currentEntryId)}
            className={`custom-select ${styles.selector}`}
          >
            {ATTEMPTS.map((attempt) => (
              <option key={attempt} value={attempt}>
                Attempt {attempt}
              </option>
            ))}
          </Form.Control>
          <Form.Control
            aria-label="Lifter"
            as="select"
            value={selectedEntry}
            onChange={this.handleLifterChange}
            className={`custom-select ${styles.selector}`}
          >
            {orderedEntries.length === 0 ? <option value="">No lifters</option> : null}
            {orderedEntries.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </Form.Control>
        </div>
        <div className={styles.actions}>
          <ButtonGroup>
            <Button
              variant="outline-primary"
              onClick={() => this.setState({ replaceTableWithWeighins: !this.state.replaceTableWithWeighins })}
            >
              Toggle Weigh-ins
            </Button>
            <Button variant="outline-secondary" onClick={this.handleFullscreen}>
              Toggle Fullscreen
            </Button>
          </ButtonGroup>
          {this.isLiveMode() ? (
            <button
              type="button"
              aria-label={barReadyLabel}
              className={styles.barReadyButton}
              disabled={this.state.liveStatus !== "connected" || currentEntryId === null}
              onClick={() => this.handleBarReady(currentEntryId, attemptOneIndexed)}
            >
              {barReadyLabel}
            </button>
          ) : null}
          {this.isLiveMode()
            ? this.renderRecordToggle(currentAttempt, "Actual", "Marcar intento actual como récord")
            : null}
          {this.isLiveMode()
            ? this.renderRecordToggle(nextAttempt, "Próximo", "Marcar próximo intento como récord")
            : null}
          {this.renderPendingVerdictControls(platformRef, currentEntryId, attemptOneIndexed, "operation")}
          <button
            type="button"
            aria-label="Manual No Lift"
            className={styles.noLiftButton}
            disabled={this.state.manualLiftLocked}
            onClick={() => this.handleMarkLift(currentEntryId, attemptOneIndexed, false)}
          >
            No Lift
          </button>
          <button
            type="button"
            aria-label="Manual Good Lift"
            className={styles.goodLiftButton}
            disabled={this.state.manualLiftLocked}
            onClick={() => this.handleMarkLift(currentEntryId, attemptOneIndexed, true)}
          >
            Good Lift
          </button>
        </div>
      </div>
    );
  };

  renderClassicLiveClock = (remainingSeconds: number | null) => {
    const running = remainingSeconds !== null;
    const seconds = running ? Math.max(0, remainingSeconds) : null;
    const danger = running && seconds !== null && seconds <= 10;
    const radius = 42;
    const circumference = 2 * Math.PI * radius;
    const progress = seconds === null ? 0 : Math.max(0, Math.min(1, seconds / 60));

    return (
      <div
        className={`${styles.classicLiveClock} ${danger ? styles.classicLiveClockDanger : ""}`}
        aria-label={danger ? "Attempt Clock danger" : "Attempt Clock"}
      >
        <svg className={styles.classicClockFace} viewBox="0 0 120 120" aria-hidden="true">
          <circle className={styles.classicClockTrack} cx="60" cy="60" r={radius} />
          {seconds !== null ? (
            <circle
              className={styles.classicClockProgress}
              cx="60"
              cy="60"
              r={radius}
              pathLength={circumference}
              strokeDasharray={circumference}
              style={{ strokeDashoffset: circumference * (1 - progress) }}
              aria-label="Attempt Clock progress"
            />
          ) : null}
        </svg>
        <div className={styles.classicClockValue} aria-label="Attempt Clock value">
          {seconds === null ? "--" : seconds}
        </div>
      </div>
    );
  };

  renderClassicVoteLights = (votes: JudgeVotes) => {
    const lightClass = (position: JudgePosition): string => {
      if (votes[position] === "good") return `${styles.voteLight} ${styles.voteLightWhite}`;
      if (votes[position] === "noLift") return `${styles.voteLight} ${styles.voteLightRed}`;
      return styles.voteLight;
    };

    return (
      <div className={styles.classicVoteLights} aria-label="Live Judge Votes">
        {JUDGE_POSITIONS.map((position) => (
          <span key={position} className={lightClass(position)} aria-label={`${position} vote light`} />
        ))}
      </div>
    );
  };

  renderClassicLiveStatus = (platformRef: PlatformRef, currentEntryId: number | null, attemptOneIndexed: number) => {
    const attemptKey =
      currentEntryId === null
        ? null
        : this.makeAttemptKey(platformRef, currentEntryId, this.getActiveLift(), attemptOneIndexed);
    const remainingSeconds = attemptKey === null ? null : this.getCurrentClockSeconds(attemptKey);
    const judgeVotingEnabled = this.isJudgeVotingEnabled();
    const judgeVotes = judgeVotingEnabled
      ? this.getJudgeVotesForAttempt(platformRef, currentEntryId, this.getActiveLift(), attemptOneIndexed)
      : {};
    const { nextAttempt } = this.getPlatformLiveAttempts();
    const nextRecordCategory = nextAttempt?.recordCategory;

    return (
      <div className={styles.classicLiveStatus}>
        <div className={styles.classicLiveMain}>
          {this.renderClassicLiveClock(remainingSeconds)}
          {judgeVotingEnabled ? this.renderClassicVoteLights(judgeVotes) : null}
          {nextAttempt?.recordAttempt === true ? (
            <div
              className={styles.classicNextRecord}
              aria-label={`Próximo: ${recordAttemptAriaLabel(nextRecordCategory)}`}
              {...recordCategoryDataAttributes(nextRecordCategory)}
            >
              Próximo: {recordAttemptLabel(nextRecordCategory)}
            </div>
          ) : null}
          {judgeVotingEnabled && this.state.pendingVerdict?.attemptKey === attemptKey ? (
            <div className={styles.classicPendingVerdict} aria-label="Pending Verdict">
              {this.state.pendingVerdict.result ? "VÁLIDO" : "NULO"} · confirmar
            </div>
          ) : null}
        </div>
        <LiveConnectionIndicators
          liveStatus={this.state.liveStatus}
          liveError={this.state.liveError}
          dmxStatus={this.state.dmxStatus}
          onReconnect={this.reconnectNow}
          compact={true}
        />
      </div>
    );
  };

  renderClassicFooter = (
    flights: Array<Flight>,
    effectiveFlight: Flight,
    currentEntryId: number | null,
    orderedEntries: Array<Entry>,
    attemptOneIndexed: number,
    platformRef: PlatformRef,
  ) => {
    const language = this.props.state.language;
    const lifting = this.props.state.lifting;
    const numPlatforms = this.props.state.meet.platformsOnDays[platformRef.day - 1] || platformRef.platformId;
    const dayTemplate = getString("lifting.footer-day-template", language);
    const platformTemplate = getString("lifting.footer-platform-template", language);
    const flightTemplate = getString("lifting.footer-flight-template", language);
    const attemptTemplate = getString("lifting.footer-attempt-template", language);
    const selectedEntry = currentEntryId === null ? "" : String(currentEntryId);

    const dayOptions = [];
    for (let day = 1; day <= this.props.state.meet.lengthDays; day++) {
      dayOptions.push(
        <option key={day} value={day}>
          {dayTemplate.replace("{N}", displayNumber(day, language))}
        </option>,
      );
    }

    const platformOptions = [];
    for (let platform = 1; platform <= numPlatforms; platform++) {
      platformOptions.push(
        <option key={platform} value={platform}>
          {platformTemplate.replace("{N}", displayNumber(platform, language))}
        </option>,
      );
    }

    const attemptOptions = [1, 2, 3];
    if (this.props.state.meet.allow4thAttempts === true) {
      attemptOptions.push(4);
    }

    return (
      <div ref={this.classicFooterRef} className={styles.classicJudgingFooter}>
        {this.renderJudgingControlsRow(platformRef, currentEntryId, attemptOneIndexed)}
        <div className={styles.classicFooterRow}>
          <div className={liftingFooterStyles.leftControls}>
            <Form.Control
              aria-label="Day"
              as="select"
              value={platformRef.day.toString()}
              disabled={true}
              className={`custom-select ${liftingFooterStyles.selector}`}
            >
              {dayOptions}
            </Form.Control>
            <Form.Control
              aria-label="Platform"
              as="select"
              value={platformRef.platformId.toString()}
              disabled={true}
              className={`custom-select ${liftingFooterStyles.selector}`}
            >
              {platformOptions}
            </Form.Control>
            <Form.Control
              aria-label="Lift"
              as="select"
              value={lifting.lift}
              onChange={this.handleClassicLiftChange}
              className={`custom-select ${liftingFooterStyles.selector}`}
            >
              <option value="S">{getString("lifting.footer-squat", language)}</option>
              <option value="B">{getString("lifting.footer-bench", language)}</option>
              <option value="D">{getString("lifting.footer-deadlift", language)}</option>
            </Form.Control>
            <Form.Control
              aria-label="Flight"
              as="select"
              value={effectiveFlight}
              onChange={this.handleClassicFlightChange}
              className={`custom-select ${liftingFooterStyles.selector}`}
            >
              {flights.length === 0 ? (
                <option value={effectiveFlight}>{getString("lifting.footer-no-flights", language)}</option>
              ) : null}
              {flights.map((flight) => (
                <option key={flight} value={flight}>
                  {flightTemplate.replace("{flight}", localizeFlight(flight, language))}
                </option>
              ))}
            </Form.Control>
            <Form.Control
              aria-label="Attempt"
              as="select"
              value={attemptOneIndexed.toString()}
              onChange={(event: React.BaseSyntheticEvent) => this.handleClassicAttemptChange(event, currentEntryId)}
              className={`custom-select ${liftingFooterStyles.selector}`}
            >
              {attemptOptions.map((attempt) => (
                <option key={attempt} value={attempt}>
                  {attemptTemplate.replace("{N}", displayNumber(attempt, language))}
                </option>
              ))}
            </Form.Control>
            <Form.Control
              aria-label="Lifter"
              as="select"
              value={selectedEntry}
              onChange={this.handleClassicLifterChange}
              className={`custom-select ${liftingFooterStyles.selector}`}
            >
              {orderedEntries.length === 0 ? (
                <option value="">{getString("lifting.footer-no-lifters", language)}</option>
              ) : null}
              {orderedEntries.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </Form.Control>
          </div>

          <div className={liftingFooterStyles.rightControls}>
            <ButtonGroup>
              <Button
                variant="outline-primary"
                onClick={() => this.setState({ replaceTableWithWeighins: !this.state.replaceTableWithWeighins })}
              >
                Toggle Weigh-ins
              </Button>
              <Button
                variant="outline-secondary"
                onClick={this.handleFullscreen}
                className={liftingFooterStyles.fullscreen}
              >
                Toggle Fullscreen
              </Button>
            </ButtonGroup>
            {this.renderManualLiftButtons(currentEntryId, attemptOneIndexed, true)}
          </div>
        </div>
      </div>
    );
  };

  // Marca manual Válido/Nulo. En el pie de Levantamientos, pasar el mouse confirma el peso que se está
  // tipeando (blur); en la barra de tarima no, para no sacarle el foco a la pantalla en la que se está.
  renderManualLiftButtons = (currentEntryId: number | null, attemptOneIndexed: number, blurOnHover: boolean) => {
    const onMouseEnter = blurOnHover ? this.handleOnMouseEnter : undefined;
    return (
      <>
        <button
          type="button"
          aria-label="Manual No Lift"
          onMouseEnter={onMouseEnter}
          onMouseDown={() => this.handleMarkLift(currentEntryId, attemptOneIndexed, false)}
          onClick={() => this.handleMarkLift(currentEntryId, attemptOneIndexed, false)}
          className={liftingFooterStyles.noLift}
          disabled={this.state.manualLiftLocked}
        >
          No Lift
        </button>
        <button
          type="button"
          aria-label="Manual Good Lift"
          onMouseEnter={onMouseEnter}
          onMouseDown={() => this.handleMarkLift(currentEntryId, attemptOneIndexed, true)}
          onClick={() => this.handleMarkLift(currentEntryId, attemptOneIndexed, true)}
          className={liftingFooterStyles.goodLift}
          disabled={this.state.manualLiftLocked}
        >
          Good Lift
        </button>
      </>
    );
  };

  getBarReadyButtonLabel = (currentAttemptKey: string | null): string => {
    if (currentAttemptKey === null) {
      return "Barra lista";
    }

    if (this.state.attemptClock?.attemptKey !== currentAttemptKey) {
      return "Barra lista";
    }

    return this.isClassicLiftingWindow() ? "Parar tiempo" : "Reiniciar temporizador";
  };

  handleBarReady = (currentEntryId: number | null, attemptOneIndexed: number) => {
    requestOperatorNotificationPermission();
    const platformRef = this.props.platformRef;
    if (platformRef === null || currentEntryId === null || this.state.liveStatus !== "connected") {
      return;
    }

    const attemptKey = this.makeAttemptKey(platformRef, currentEntryId, this.getActiveLift(), attemptOneIndexed);
    if (this.isClassicLiftingWindow() && this.state.attemptClock?.attemptKey === attemptKey) {
      this.sendLive({
        type: "stopAttemptClock",
        day: platformRef.day,
        platformId: platformRef.platformId,
        attemptKey,
      });
      this.stopClockTicker();
      this.setState({
        attemptClock: null,
        serverNowMs: null,
        clockReceivedAtMs: null,
        clockTick: Date.now(),
        judgeVotes: {},
        judgeVotesAttemptKey: null,
      });
      return;
    }

    this.sendLive({
      type: "barReady",
      day: platformRef.day,
      platformId: platformRef.platformId,
      attemptKey,
    });
  };

  // Live judging controls embedded in the classic lifting footer. The classic surface keeps only the
  // primary bar-ready control; detailed clock and Judge Vote status stay in the live status panel.
  renderJudgingControlsRow = (platformRef: PlatformRef, currentEntryId: number | null, attemptOneIndexed: number) => {
    const currentAttemptKey =
      currentEntryId === null
        ? null
        : this.makeAttemptKey(platformRef, currentEntryId, this.getActiveLift(), attemptOneIndexed);
    const hasClock = currentAttemptKey !== null && this.state.attemptClock?.attemptKey === currentAttemptKey;
    const connected = this.state.liveStatus === "connected";
    const judgeVotingEnabled = this.isJudgeVotingEnabled();
    // "Manual next" hold: a verdict was committed and the operator keeps seeing it (lights + VÁLIDO/NULO) +
    // the "Siguiente" button until they advance. Derived from the Redux override (isHoldingForNext) so it
    // SURVIVES changing screens; the verdict text falls back to the held attempt's scoresheet status when the
    // component-only heldVerdict (with the per-judge lights) is gone after a remount.
    const awaitingNext = this.isHoldingForNext();
    const heldVerdict = this.state.heldVerdict;
    const heldStatus = this.heldOverrideStatus();
    const liveVotes =
      judgeVotingEnabled && currentEntryId !== null
        ? this.getJudgeVotesForAttempt(platformRef, currentEntryId, this.getActiveLift(), attemptOneIndexed)
        : {};
    const pendingVerdictForAttempt =
      this.state.pendingVerdict !== null && this.state.pendingVerdict.attemptKey === currentAttemptKey;
    const verdict =
      awaitingNext && heldVerdict !== null
        ? heldVerdict.result
        : awaitingNext && heldStatus !== 0
          ? heldStatus === 1
          : !awaitingNext && judgeVotingEnabled && !pendingVerdictForAttempt
            ? resolveJudgeVotes(liveVotes)
            : null;
    const barReadyLabel = hasClock ? "Parar tiempo" : "Barra lista";

    return (
      <div className={styles.judgingRow}>
        <button
          type="button"
          aria-label={barReadyLabel}
          className={styles.classicBarReady}
          disabled={!connected || currentEntryId === null || awaitingNext}
          onClick={() => this.handleBarReady(currentEntryId, attemptOneIndexed)}
        >
          {barReadyLabel}
        </button>
        {verdict !== null ? (
          <span
            className={`${styles.judgingVerdict} ${verdict ? styles.judgingVerdictGood : styles.judgingVerdictNoLift}`}
            aria-label="Verdict"
          >
            {verdict ? "VÁLIDO" : "NULO"}
          </span>
        ) : null}
        {this.renderPendingVerdictControls(platformRef, currentEntryId, attemptOneIndexed, "classic")}
        {awaitingNext ? (
          <button type="button" aria-label="Siguiente" className={styles.nextButton} onClick={this.handleNextLifter}>
            Siguiente <FontAwesomeIcon icon={faForward} />
          </button>
        ) : null}
      </div>
    );
  };

  formatOperatorDockWeight = (weightKg: number): string => {
    const { inKg } = this.props.state.meet;
    const weight = displayWeightOnePlace(inKg ? weightKg : kg2lbs(weightKg), this.props.state.language);
    return `${weight} ${inKg ? "kg" : "lb"}`;
  };

  // Barra de tarima: lo justo para seguir la tanda desde cualquier pantalla. Reusa los controles del pie de
  // Levantamientos (renderJudgingControlsRow) para no tener una segunda lógica que se desincronice.
  renderOperatorDock = () => {
    const platformRef = this.props.platformRef;
    const context = platformRef === null || !this.props.isValidPlatform ? null : this.getCurrentAttemptContext();
    if (platformRef === null || context === null) {
      return null;
    }

    const language = this.props.state.language;
    const liftName = (lift: Lift): string =>
      getString(
        lift === "S" ? "lifting.footer-squat" : lift === "B" ? "lifting.footer-bench" : "lifting.footer-deadlift",
        language,
      );
    const { currentAttempt, nextAttempt } = this.getPlatformLiveAttempts();
    const judgeVotingEnabled = this.isJudgeVotingEnabled();
    const clockSeconds = currentAttempt === null ? null : this.getCurrentClockSeconds(currentAttempt.key);
    const remaining = clockSeconds === null ? 0 : Math.max(0, Math.ceil(clockSeconds));
    const clock =
      clockSeconds === null ? "--" : `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;
    const judgeVotes =
      judgeVotingEnabled && context.currentEntryId !== null
        ? this.getJudgeVotesForAttempt(
            platformRef,
            context.currentEntryId,
            this.getActiveLift(),
            context.attemptOneIndexed,
          )
        : {};

    return (
      <>
        <div
          className={styles.operatorDockSpacer}
          style={{ height: this.state.classicFooterHeight }}
          aria-hidden="true"
        />
        <section
          ref={this.operatorDockRef}
          className={[
            styles.operatorDock,
            this.isHoldingForNext() ? styles.operatorDockPending : "",
            this.isHoldingForNext() && !this.isHeldVerdictGood() ? styles.operatorDockPendingNoLift : "",
          ].join(" ")}
          aria-label="Tarima en vivo"
        >
          <LiveConnectionIndicators
            liveStatus={this.state.liveStatus}
            liveError={this.state.liveError}
            dmxStatus={this.state.dmxStatus}
            onReconnect={this.reconnectNow}
            compact={true}
          />
          <div className={styles.operatorDockLifters}>
            <p>
              {currentAttempt === null
                ? "Sin atleta en curso"
                : `${currentAttempt.lifterName} · ${liftName(currentAttempt.lift)} ${currentAttempt.attempt}° · ${this.formatOperatorDockWeight(currentAttempt.weightKg)}`}
            </p>
            {nextAttempt === null ? null : (
              <p className={styles.operatorDockNext}>
                Sigue: {nextAttempt.lifterName} · {this.formatOperatorDockWeight(nextAttempt.weightKg)}
              </p>
            )}
          </div>
          <span className={styles.operatorDockClock} aria-label="Attempt Clock value">
            {clock}
          </span>
          {judgeVotingEnabled ? this.renderClassicVoteLights(judgeVotes) : null}
          {this.renderJudgingControlsRow(platformRef, context.currentEntryId, context.attemptOneIndexed)}
          <div className={styles.operatorDockManual}>
            {this.renderManualLiftButtons(context.currentEntryId, context.attemptOneIndexed, false)}
          </div>
          <Link className={styles.operatorDockLink} to={{ pathname: "/", search: this.props.locationSearch }}>
            Ir a Levantamientos
          </Link>
        </section>
      </>
    );
  };

  renderClassicLiftingWindow = (platformRef: PlatformRef, flights: Array<Flight>, context: CurrentAttemptContext) => {
    const rightElement = this.state.replaceTableWithWeighins ? (
      <WeighinsView day={platformRef.day} platform={platformRef.platformId} inLiftingPage={true} />
    ) : (
      <LiftingTable
        attemptOneIndexed={context.attemptOneIndexed}
        orderedEntries={context.orderedEntries}
        currentEntryId={context.currentEntryId}
        isRecordAttempt={this.isRecordAttemptForEntry}
        isRecordCandidate={this.isRecordCandidateForEntry}
        getRecordCategory={this.getRecordCategoryForEntry}
        getRecordCandidateCategory={this.getRecordCandidateCategoryForEntry}
        onAttemptRecordContextMenu={this.isLiveMode() ? this.handleAttemptRecordContextMenu : undefined}
      />
    );

    return (
      <>
        <div
          id="liftingView"
          className={liftingViewStyles.liftingView}
          style={
            this.state.classicFooterHeight > 0 ? { paddingBottom: this.state.classicFooterHeight + 16 } : undefined
          }
        >
          <LiftingHeader
            attemptOneIndexed={context.attemptOneIndexed}
            orderedEntries={context.orderedEntries}
            currentEntryId={context.currentEntryId}
          />

          <div className={liftingViewStyles.middleParentContainer}>
            <div className={liftingViewStyles.leftCardContainer}>
              <LeftCard
                attemptOneIndexed={context.attemptOneIndexed}
                orderedEntries={context.orderedEntries}
                currentEntryId={context.currentEntryId}
                nextEntryId={context.nextEntryId}
                nextAttemptOneIndexed={context.nextAttemptOneIndexed}
                isRecordAttempt={(entryId, attemptOneIndexed) =>
                  entryId !== null &&
                  attemptOneIndexed !== null &&
                  this.isRecordAttemptForEntry(entryId, this.getActiveLift(), attemptOneIndexed)
                }
                getRecordCategory={(entryId, attemptOneIndexed) =>
                  entryId === null || attemptOneIndexed === null
                    ? undefined
                    : this.getRecordCategoryForEntry(entryId, this.getActiveLift(), attemptOneIndexed)
                }
              />
              {this.renderClassicLiveStatus(platformRef, context.currentEntryId, context.attemptOneIndexed)}
            </div>

            <div className={liftingViewStyles.rightCardContainer}>{rightElement}</div>
          </div>

          {this.renderClassicFooter(
            flights,
            context.effectiveFlight,
            context.currentEntryId,
            context.orderedEntries,
            context.attemptOneIndexed,
            platformRef,
          )}
        </div>
        {this.renderRecordContextMenu()}
      </>
    );
  };

  render() {
    // OperatorLiveHost fuera de Levantamientos: la sesión en vivo vive en los ciclos de vida, no en el render.
    if (this.props.presentation === "hidden") {
      return null;
    }
    if (this.props.presentation === "dock") {
      return this.renderOperatorDock();
    }

    const platformRef = this.props.platformRef;
    if (platformRef === null || !this.props.isValidPlatform) {
      return this.renderInvalid();
    }

    const flights = this.getFlights();
    const context = this.getCurrentAttemptContext();
    if (context === null) {
      return this.renderInvalid();
    }

    if (this.isClassicLiftingWindow()) {
      return this.renderClassicLiftingWindow(platformRef, flights, context);
    }

    const rightElement = this.state.replaceTableWithWeighins ? (
      <WeighinsView day={platformRef.day} platform={platformRef.platformId} inLiftingPage={true} />
    ) : (
      this.renderTable(context.orderedEntries, context.currentEntryId)
    );

    return (
      <>
        <div id="wrpfOperationLayer" className={styles.operationLayer}>
          <div className={styles.topBar}>
            <h1 className={styles.title}>Operation Layer</h1>
            <div className={styles.platformLock}>
              Day {platformRef.day} - Platform {platformRef.platformId}
            </div>
          </div>
          <div className={styles.mainGrid}>
            <div className={styles.leftPanel}>
              <div className={styles.currentLifter}>
                <div className={styles.currentName}>
                  {context.currentEntry === null ? "Flight Complete" : context.currentEntry.name}
                </div>
                <div className={styles.currentMeta}>
                  {this.getActiveLift()}
                  {context.attemptOneIndexed} - Flight {context.effectiveFlight}
                </div>
              </div>
              {this.renderBarLoad(context.currentEntry, context.attemptOneIndexed)}
              {this.renderAttemptClock(platformRef, context.currentEntryId, context.attemptOneIndexed)}
              {this.renderJudgeVoting(platformRef, context.currentEntryId, context.attemptOneIndexed)}
            </div>
            <div className={styles.rightPanel}>{rightElement}</div>
            <div className={styles.monitor} aria-label="Cross-Platform Monitor">
              <h2>Cross-Platform Monitor</h2>
              <p>Read-only placeholder for same-day platform visibility. No sync or write controls are active here.</p>
            </div>
          </div>
          {this.renderFooter(
            flights,
            context.effectiveFlight,
            context.currentEntryId,
            context.orderedEntries,
            context.attemptOneIndexed,
            platformRef,
          )}
        </div>
        {this.renderRecordContextMenu()}
      </>
    );
  }
}

export default OperationLayerView;
